import { FC } from 'react'
import { Skeleton } from 'src/components/Skeleton'
import styles from './sentimentScoreCard.module.scss'

// One skeleton per legend entry (positive / neutral / negative).
const LEGEND_ITEMS = 3

interface SentimentScoreCardProps {
  hasData?: boolean
  isLoading?: boolean
  negativePercentage?: number
  neutralPercentage?: number
  positivePercentage?: number
  score?: number
}

const getLabel = (
  score: number
): { bg: string; color: string; label: string } => {
  if (score >= 60) return { bg: '#EEF0FE', color: '#8080FF', label: 'Positive' }
  if (score >= 40) return { bg: '#FFF3E8', color: '#FA8C3A', label: 'Neutral' }
  return { bg: '#FFF0EB', color: '#FA7138', label: 'Poor' }
}

export const SentimentScoreCard: FC<SentimentScoreCardProps> = ({
  hasData = false,
  isLoading = false,
  negativePercentage = 0,
  neutralPercentage = 0,
  positivePercentage = 0,
  score = 0,
}) => {
  const { label, color, bg } = getLabel(score)

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <svg
          className={styles.icon}
          fill="none"
          height="24"
          viewBox="0 0 24 24"
          width="24"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="#25262D"
            strokeOpacity="0.8"
            strokeWidth="1.5"
          />
          <path
            d="M8.5 14.5s1.5 2 3.5 2 3.5-2 3.5-2"
            stroke="#25262D"
            strokeLinecap="round"
            strokeOpacity="0.8"
            strokeWidth="1.5"
          />
          <circle cx="9" cy="10" fill="#25262D" fillOpacity="0.8" r="1" />
          <circle cx="15" cy="10" fill="#25262D" fillOpacity="0.8" r="1" />
        </svg>
        <h3 className={styles.title}>Sentiment Score</h3>
      </div>

      {/* Loading first. While in flight every percentage is 0, so score is 0 —
          which getLabel() turns into a confident "Poor" verdict on a campaign
          whose sentiment we have not received. .scoreNum is 36px, .scoreBadge
          ~25px tall / radius 33, .barTrack 6px / radius 16, .legendItem 11px
          (sentimentScoreCard.module.scss). */}
      {isLoading ? (
        <>
          <div className={styles.scoreRow}>
            <Skeleton height={36} width={96} />
          </div>
          <Skeleton height={6} radius={16} />
          <div className={styles.legend}>
            {Array.from({ length: LEGEND_ITEMS }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton height={11} key={index} width={72} />
            ))}
          </div>
        </>
      ) : hasData ? (
        <>
          {/* Score + label */}
          <div className={styles.scoreRow}>
            <span className={styles.scoreNum}>{score}</span>
            <span className={styles.scoreOutOf}>&nbsp;/ 100</span>
            <span
              className={styles.scoreBadge}
              style={{ background: bg, color }}
            >
              {label}
            </span>
          </div>

          {/* Bar */}
          <div className={styles.barTrack}>
            <div
              className={styles.barPositive}
              style={{ width: `${positivePercentage}%` }}
            />
            <div
              className={styles.barNeutral}
              style={{ width: `${neutralPercentage}%` }}
            />
            <div
              className={styles.barNegative}
              style={{ width: `${negativePercentage}%` }}
            />
          </div>

          {/* Legend */}
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#8080FF' }} />
              Positive {positivePercentage}%
            </span>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#C7CED1' }} />
              Neutral {neutralPercentage}%
            </span>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#FA7138' }} />
              Negative {negativePercentage}%
            </span>
          </div>
        </>
      ) : (
        <div className={styles.noData}>No Data</div>
      )}
    </div>
  )
}
